/**
 * Error elements for the router: a page whose code failed to load (stale chunk after a deploy) or that threw while
 * rendering. App.tsx imports this statically: it must work exactly when lazy chunks don't.
 */
import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button, EmptyState, LinkButton, Loading } from '@/components/ui';
import { isChunkLoadError, isReloadingForStaleChunk, reloadForStaleChunk } from '@/lib/chunkReload';

/** Rendered inside Layout's <Outlet>, so the nav stays usable. */
export function RouteErrorPage() {
  return <RouteErrorView error={useRouteError()} />;
}

/** Last resort when Layout itself failed: same content without the app chrome. */
export function RootErrorPage() {
  return (
    <div className="app-shell">
      <main className="main">
        <RouteErrorView error={useRouteError()} />
      </main>
    </div>
  );
}

function errorText(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`.trim();
  if (error instanceof Error) return error.message;
  return String(error);
}

export function RouteErrorView({ error }: { error: unknown }) {
  const stale = isChunkLoadError(error);
  const [reloading, setReloading] = useState(isReloadingForStaleChunk);

  // A page from an older deploy: fetch the current version once, automatically.
  useEffect(() => {
    if (stale && reloadForStaleChunk()) setReloading(true);
  }, [stale]);

  if (reloading) {
    return (
      <div className="page">
        <Loading label="Updating Taretto…" />
      </div>
    );
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div className="page">
        <EmptyState icon="coffee" title="Nothing brewing here" action={<LinkButton to="/" variant="primary">Go home</LinkButton>}>
          That page doesn&apos;t exist.
        </EmptyState>
      </div>
    );
  }

  const actions = (
    <div className="row-wrap" style={{ justifyContent: 'center' }}>
      <Button variant="primary" icon="refresh" onClick={() => window.location.reload()}>
        Reload
      </Button>
      <LinkButton to="/" variant="ghost">
        Go home
      </LinkButton>
    </div>
  );

  return (
    <div className="page" role="alert">
      {stale ? (
        <EmptyState icon="refresh" title="Couldn’t load this page" action={actions}>
          Taretto was probably updated since this tab opened, or the server can’t be reached right now. Reloading fetches the current version.
        </EmptyState>
      ) : (
        <EmptyState icon="alert" title="Something went wrong" action={actions}>
          <span className="stack-sm">
            <span>This page hit an unexpected error. Reloading usually fixes it; your saved data is safe.</span>
            <span className="mono text-xs faint">{errorText(error)}</span>
          </span>
        </EmptyState>
      )}
    </div>
  );
}
